import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api, { STATUS_STYLES, AU_STATES, shortDate } from "@/lib/api";
import { Plus, Search, X } from "lucide-react";
import { toast } from "sonner";

export default function Customers() {
  const [customers, setCustomers] = useState([]);
  const [search, setSearch] = useState("");
  const [state, setState] = useState("");
  const [status, setStatus] = useState("");
  const [agents, setAgents] = useState([]);
  const [showNew, setShowNew] = useState(false);

  const load = () => {
    const params = {};
    if (search) params.search = search;
    if (state) params.state = state;
    if (status) params.status = status;
    api.get("/customers", { params }).then(r => setCustomers(r.data));
  };

  useEffect(() => {
    api.get("/agents").then(r => setAgents(r.data)).catch(() => {});
  }, []);
  useEffect(() => { load(); }, [search, state, status]);

  return (
    <div className="space-y-6" data-testid="customers-page">
      <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-3">
        <div>
          <h1 className="text-3xl sm:text-4xl font-heading font-black tracking-tight">Customers & Stores</h1>
          <p className="text-slate-600 mt-1">{customers.length} accounts</p>
        </div>
        <button className="mlily-btn h-11 px-5 rounded-lg font-semibold flex items-center gap-2" onClick={() => setShowNew(true)} data-testid="new-customer-btn">
          <Plus className="w-4 h-4" /> New Customer
        </button>
      </div>

      <div className="bg-white rounded-xl border border-slate-200 p-4 flex flex-wrap gap-3">
        <div className="relative flex-1 min-w-[220px]">
          <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
          <input
            data-testid="customers-search"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search name, account #, town..."
            className="w-full h-11 pl-9 pr-3 border border-slate-300 rounded-lg"
          />
        </div>
        <select value={state} onChange={(e) => setState(e.target.value)} className="h-11 px-3 border border-slate-300 rounded-lg" data-testid="customers-state">
          <option value="">All States</option>
          {AU_STATES.map(s => <option key={s}>{s}</option>)}
        </select>
        <select value={status} onChange={(e) => setStatus(e.target.value)} className="h-11 px-3 border border-slate-300 rounded-lg" data-testid="customers-status">
          <option value="">All Statuses</option>
          {["Active", "Inactive", "No Sale This Month", "Follow-up Required", "New Customer", "Returning Customer"].map(s => <option key={s}>{s}</option>)}
        </select>
      </div>

      <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
        {/* Desktop table */}
        <div className="hidden md:block overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-slate-50 text-xs uppercase tracking-wide text-slate-500">
              <tr>
                <th className="text-left px-4 py-3">Account #</th>
                <th className="text-left px-4 py-3">Name / Store</th>
                <th className="text-left px-4 py-3">Town</th>
                <th className="text-left px-4 py-3">State</th>
                <th className="text-left px-4 py-3">Status</th>
                <th className="text-left px-4 py-3">Last Order</th>
                <th className="text-left px-4 py-3">Last Visit</th>
              </tr>
            </thead>
            <tbody>
              {customers.map((c) => (
                <tr key={c.id} className="border-t hover:bg-slate-50 transition" data-testid={`customer-row-${c.account_number}`}>
                  <td className="px-4 py-3 font-mono text-xs">{c.account_number}</td>
                  <td className="px-4 py-3">
                    <Link to={`/customers/${c.id}`} className="font-semibold hover:text-[#003DA5]">{c.name}</Link>
                    <div className="text-xs text-slate-500">{c.store_name}</div>
                  </td>
                  <td className="px-4 py-3">{c.town}</td>
                  <td className="px-4 py-3">{c.state}</td>
                  <td className="px-4 py-3"><span className={`text-xs px-2 py-1 rounded-full ${STATUS_STYLES[c.status] || "badge-inactive"}`}>{c.status}</span></td>
                  <td className="px-4 py-3">{shortDate(c.last_order_date)}</td>
                  <td className="px-4 py-3">{shortDate(c.last_visit_date)}</td>
                </tr>
              ))}
              {customers.length === 0 && <tr><td colSpan="7" className="px-4 py-10 text-center text-slate-500">No customers found.</td></tr>}
            </tbody>
          </table>
        </div>

        {/* Mobile cards */}
        <div className="md:hidden divide-y divide-slate-100">
          {customers.map((c) => (
            <Link to={`/customers/${c.id}`} key={c.id} className="block px-4 py-3 hover:bg-slate-50" data-testid={`customer-card-${c.account_number}`}>
              <div className="flex items-start justify-between">
                <div>
                  <div className="font-semibold">{c.name}</div>
                  <div className="text-xs text-slate-500">{c.town}, {c.state}</div>
                </div>
                <span className={`text-[10px] px-2 py-1 rounded-full ${STATUS_STYLES[c.status] || "badge-inactive"}`}>{c.status}</span>
              </div>
              <div className="flex justify-between mt-2 text-xs text-slate-500">
                <span>Last order: {shortDate(c.last_order_date)}</span>
                <span>Last visit: {shortDate(c.last_visit_date)}</span>
              </div>
            </Link>
          ))}
        </div>
      </div>

      {showNew && <NewCustomerModal agents={agents} onClose={() => setShowNew(false)} onSaved={() => { setShowNew(false); load(); }} />}
    </div>
  );
}

function NewCustomerModal({ agents, onClose, onSaved }) {
  const [f, setF] = useState({ account_number: "", name: "", store_name: "", town: "", state: "NSW", postcode: "", agent_id: "", contact_person: "", email: "", phone: "", customer_type: "Retail" });
  const [busy, setBusy] = useState(false);
  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await api.post("/customers", f);
      toast.success("Customer created");
      onSaved();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to create");
    } finally { setBusy(false); }
  };
  const upd = (k) => (e) => setF({ ...f, [k]: e.target.value });
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
      <div className="bg-white rounded-xl w-full max-w-2xl max-h-[92vh] overflow-y-auto">
        <div className="flex items-center justify-between p-5 border-b sticky top-0 bg-white">
          <h3 className="text-xl font-heading font-semibold">New Customer</h3>
          <button onClick={onClose}><X className="w-5 h-5" /></button>
        </div>
        <form onSubmit={submit} className="p-5 grid grid-cols-1 sm:grid-cols-2 gap-4">
          <Field label="Account Number *" value={f.account_number} onChange={upd("account_number")} required testid="new-customer-account" />
          <Field label="Customer Name *" value={f.name} onChange={upd("name")} required testid="new-customer-name" />
          <Field label="Store Name" value={f.store_name} onChange={upd("store_name")} />
          <Field label="Town" value={f.town} onChange={upd("town")} />
          <div>
            <label className="text-xs uppercase tracking-wider font-semibold text-slate-500">State</label>
            <select value={f.state} onChange={upd("state")} className="mt-1 w-full h-11 px-3 border border-slate-300 rounded-lg">
              {AU_STATES.map(s => <option key={s}>{s}</option>)}
            </select>
          </div>
          <Field label="Postcode" value={f.postcode} onChange={upd("postcode")} />
          <div>
            <label className="text-xs uppercase tracking-wider font-semibold text-slate-500">Assigned Agent</label>
            <select value={f.agent_id} onChange={upd("agent_id")} className="mt-1 w-full h-11 px-3 border border-slate-300 rounded-lg">
              <option value="">— None —</option>
              {agents.map(a => <option key={a.id} value={a.id}>{a.name}</option>)}
            </select>
          </div>
          <Field label="Contact Person" value={f.contact_person} onChange={upd("contact_person")} />
          <Field label="Email" type="email" value={f.email} onChange={upd("email")} />
          <Field label="Phone" value={f.phone} onChange={upd("phone")} />
          <Field label="Customer Type" value={f.customer_type} onChange={upd("customer_type")} />
          <div className="col-span-full flex justify-end gap-2 pt-4 border-t">
            <button type="button" className="h-11 px-5 border border-slate-300 rounded-lg" onClick={onClose}>Cancel</button>
            <button type="submit" disabled={busy} className="mlily-btn h-11 px-5 rounded-lg font-semibold" data-testid="save-customer-btn">
              {busy ? "Saving…" : "Save Customer"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

function Field({ label, testid, ...rest }) {
  return (
    <div>
      <label className="text-xs uppercase tracking-wider font-semibold text-slate-500">{label}</label>
      <input {...rest} data-testid={testid} className="mt-1 w-full h-11 px-3 border border-slate-300 rounded-lg" />
    </div>
  );
}
